import type { MouseEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { LuCalendar } from 'react-icons/lu';

import type { DateInputProps, RangeDateValue, SingleDateValue } from './types';
import {
    countDigitsBeforeIndex,
    formatDateInput,
    formatMonthInput,
    formatSingleDate,
    formatSingleMonth,
    getCursorPositionForDigitCount,
    parseDate,
    parseMonth,
    toUtcMidnight,
} from './util';
import { Calendar } from '../../general/Calendar';
import { Label } from '../../general/Label';
import { COMPONENTS_NAMESPACE } from '../../locales';
import { cn } from '../../utils/cn';
import { handleEventBehavior } from '../../utils/EventBehavior';

export type { DateInputProps } from './types';

type RangeField = 'from' | 'to';

/**
 * A form date input component that combines a label and date picker input.
 *
 * @param label - Label text for the input.
 * @param id - ID of the input element. Used for htmlFor association.
 * @param mode - Selection mode: "single" (default) or "range".
 * @param layout - Layout direction: "horizontal" (default) or "vertical".
 * @param hideDays - Whether to hide the days and only render month/year selection with accept button.
 * @param value - The selected date value.
 * @param onChange - Callback fired when the date changes.
 * @param placeholder - Placeholder text. Defaults to "TT.MM.JJJJ" or "TT.MM.JJJJ - TT.MM.JJJJ" for range.
 * @param disabled - Whether the input is disabled.
 * @param showDropdowns - When true, shows month/year as dropdown menus for quicker selection.
 * @param className - Additional CSS classes for the container.
 * @param labelClassName - Additional CSS classes for the label.
 * @param inputClassName - Additional CSS classes for the input.
 * @param ...props - Additional HTML input attributes.
 *
 * @example
 * // Single date with horizontal layout
 * <DateInput label="Datum" id="date" value={date} onChange={setDate} />
 *
 * @example
 * // Date range with vertical layout
 * <DateInput
 *   label="Zeitraum"
 *   id="dateRange"
 *   mode="range"
 *   layout="vertical"
 *   value={range}
 *   onChange={setRange}
 * />
 *
 * @example
 * // With dropdown menus for month/year selection
 * <DateInput label="Datum" id="date" value={date} onChange={setDate} showDropdowns />
 *
 * @example
 * // Disabled state
 * <DateInput label="Datum" id="date" disabled />
 *
 * @example
 * // Hiding days and only rendering month/year selection with accept button
 * <DateInput label="Datum" id="date" hideDays />
 */
export const DateInput: React.FC<DateInputProps> = ({
    className,
    disabled,
    error,
    eventPreventDefault,
    eventStopPropagation,
    hideDays = false,
    id,
    inputClassName,
    label,
    labelClassName,
    layout = 'horizontal',
    maxAllowedYear,
    mode = 'single',
    onChange,
    onOpenChange,
    placeholder,
    showDropdowns,
    value,
    ...props
}) => {
    const { t } = useTranslation(COMPONENTS_NAMESPACE);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState<string>('');
    const [activeRangeField, setActiveRangeField] = useState<RangeField>('from');
    const inputRef = useRef<HTMLInputElement>(null);
    const pendingCursorPositionRef = useRef<number | null>(null);
    const [hasValidationError, setHasValidationError] = useState(false);

    const singlePlaceholder = t('dateInput.placeholder');
    let defaultPlaceholder = singlePlaceholder;
    if (hideDays) {
        defaultPlaceholder = t('dateInput.monthPlaceholder');
    } else if (mode === 'range') {
        defaultPlaceholder = t('dateInput.rangePlaceholder');
    }
    const actualPlaceholder = placeholder ?? defaultPlaceholder;

    useEffect(() => {
        if (hideDays) {
            setInputValue(formatSingleMonth(value as SingleDateValue));
        } else if (mode === 'single') {
            setInputValue(formatSingleDate(value as SingleDateValue));
        }
    }, [value, mode, hideDays]);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (mode !== 'single') return;

            const rawValue = e.target.value;
            const cursorPosition = e.target.selectionStart ?? rawValue.length;
            const formattedValue = hideDays ? formatMonthInput(rawValue) : formatDateInput(rawValue);

            const digitsBeforeCursor = countDigitsBeforeIndex(rawValue, cursorPosition);
            pendingCursorPositionRef.current = getCursorPositionForDigitCount(formattedValue, digitsBeforeCursor);
            setInputValue(formattedValue);

            const parsedDate = hideDays
                ? parseMonth(formattedValue, maxAllowedYear)
                : parseDate(formattedValue, maxAllowedYear);
            if (parsedDate) {
                setHasValidationError(false);
                (onChange as (date: SingleDateValue) => void)?.(parsedDate);
            } else if (formattedValue === '') {
                setHasValidationError(false);
                (onChange as (date: SingleDateValue) => void)?.(null);
            } else {
                setHasValidationError(true);
            }
        },
        [onChange, mode, hideDays],
    );

    useLayoutEffect(() => {
        if (pendingCursorPositionRef.current === null || !inputRef.current) return;

        inputRef.current.setSelectionRange(pendingCursorPositionRef.current, pendingCursorPositionRef.current);
        pendingCursorPositionRef.current = null;
    }, [inputValue]);

    const handleSingleDateSelect = useCallback(
        (date: Date | undefined) => {
            if (date) {
                (onChange as (date: SingleDateValue) => void)?.(toUtcMidnight(date));
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setOpen(false);
                    });
                });
            } else {
                (onChange as (date: SingleDateValue) => void)?.(null);
            }
        },
        [onChange],
    );

    const handleRangeDayClick = useCallback(
        (day: Date) => {
            const rangeValue = value as RangeDateValue;
            const normalizedDay = toUtcMidnight(day);
            let newRange: DateRange;

            if (activeRangeField === 'from') {
                const to = rangeValue?.to;
                if (to && normalizedDay > toUtcMidnight(to)) {
                    newRange = { from: toUtcMidnight(to), to: normalizedDay };
                } else {
                    newRange = { from: normalizedDay, to: to ? toUtcMidnight(to) : undefined };
                }
                setActiveRangeField('to');
            } else {
                const from = rangeValue?.from;
                if (from && normalizedDay < toUtcMidnight(from)) {
                    newRange = { from: normalizedDay, to: toUtcMidnight(from) };
                } else {
                    newRange = {
                        from: from ? toUtcMidnight(from) : undefined,
                        to: normalizedDay,
                    };
                }
                setActiveRangeField('from');
            }

            (onChange as (range: RangeDateValue) => void)?.(newRange);
        },
        [onChange, value, activeRangeField],
    );

    const handleClick = useCallback(
        (e: MouseEvent) => {
            if (hideDays) return;

            handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
            if (!disabled) {
                setOpen(true);
            }
        },
        [disabled, eventPreventDefault, eventStopPropagation],
    );

    const handleOpenChange = useCallback(
        (newOpen: boolean) => {
            if (disabled) return;
            setOpen(newOpen);
            onOpenChange?.(newOpen);
        },
        [disabled, onOpenChange],
    );

    const handleRangeFieldClick = useCallback(
        (field: RangeField) => (e: MouseEvent) => {
            e.stopPropagation();
            handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
            setActiveRangeField(field);
            if (!disabled) {
                setOpen(true);
            }
        },
        [disabled, eventPreventDefault, eventStopPropagation],
    );

    const calendarProps = useMemo(() => {
        if (mode === 'range') {
            const rangeValue = value as RangeDateValue;
            return {
                defaultMonth: rangeValue?.from,
                mode: 'range' as const,
                onDayClick: handleRangeDayClick,
                onSelect: () => {},
                selected: rangeValue,
            };
        }
        const singleValue = (value as SingleDateValue) || undefined;
        return {
            defaultMonth: singleValue,
            hideNavigation: hideDays,
            mode: 'single' as const,
            onSelect: handleSingleDateSelect,
            selected: singleValue,
        };
    }, [mode, value, handleSingleDateSelect, handleRangeDayClick]);

    const isHorizontal = layout === 'horizontal';
    const hasError = Boolean(error);

    const inputWrapperClassName = cn(
        'h-8 w-full',
        'flex items-center',
        'pl-3',
        'bg-white border rounded',
        hasError ? 'border-alert-error' : 'border-grey-dark',
        'focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-focus focus-within:ring-offset-1',
        disabled && 'cursor-not-allowed bg-surface-light opacity-50',
        !disabled && 'cursor-pointer hover:border-grey-dark',
        inputClassName,
    );

    const rangeTriggerElement = useMemo(() => {
        const rangeValue = value as RangeDateValue;
        const fromText = rangeValue?.from ? formatSingleDate(rangeValue.from) : '';
        const toText = rangeValue?.to ? formatSingleDate(rangeValue.to) : '';

        return (
            <div
                className={cn('relative flex items-center min-w-0', isHorizontal ? 'flex-1' : 'w-full')}
                onClick={handleClick}
            >
                <div className={cn(inputWrapperClassName, 'min-w-0')}>
                    <div className="flex items-center flex-1 min-w-0">
                        <button
                            type="button"
                            data-testid="range-from"
                            className={cn(
                                'text-sm px-1 rounded transition-colors truncate min-w-0',
                                open && activeRangeField === 'from'
                                    ? 'bg-surface-light font-medium text-midnight'
                                    : 'text-midnight',
                                !fromText && 'text-placeholder-text',
                                disabled && 'cursor-not-allowed',
                            )}
                            onClick={handleRangeFieldClick('from')}
                            disabled={disabled}
                        >
                            {fromText || singlePlaceholder}
                        </button>
                        <span className="text-sm text-grey select-none shrink-0">–</span>
                        <button
                            type="button"
                            data-testid="range-to"
                            className={cn(
                                'text-sm px-1 rounded transition-colors truncate min-w-0',
                                open && activeRangeField === 'to'
                                    ? 'bg-surface-light font-medium text-midnight'
                                    : 'text-midnight',
                                !toText && 'text-placeholder-text',
                                disabled && 'cursor-not-allowed',
                            )}
                            onClick={handleRangeFieldClick('to')}
                            disabled={disabled}
                        >
                            {toText || singlePlaceholder}
                        </button>
                    </div>
                    <span className="border-l border-grey-dark h-4/6"></span>
                    <span
                        className={cn(
                            'flex items-center justify-center h-5 w-8 shrink-0',
                            'text-midnight',
                            disabled && 'opacity-50',
                        )}
                        aria-hidden="true"
                    >
                        <LuCalendar className="size-5" />
                    </span>
                </div>
            </div>
        );
    }, [
        value,
        open,
        activeRangeField,
        isHorizontal,
        handleClick,
        handleRangeFieldClick,
        disabled,
        inputWrapperClassName,
        singlePlaceholder,
    ]);

    const singleTriggerElement = (
        <div className={cn('relative flex items-center', isHorizontal ? 'flex-1' : 'w-full')} onClick={handleClick}>
            <div className={inputWrapperClassName}>
                <input
                    ref={inputRef}
                    id={id}
                    type="text"
                    placeholder={actualPlaceholder}
                    value={inputValue}
                    onChange={handleInputChange}
                    disabled={disabled}
                    className={cn(
                        'flex-1 min-w-0 h-full',
                        'bg-transparent border-none outline-none',
                        'text-sm text-midnight',
                        'placeholder:text-placeholder-text',
                        'disabled:cursor-not-allowed',
                    )}
                    {...props}
                />
                <span className="border-l border-grey-dark h-4/6"></span>
                <span
                    className={cn(
                        'flex items-center justify-center h-5 w-8 shrink-0',
                        'text-midnight',
                        disabled && 'opacity-50',
                    )}
                    aria-hidden="true"
                >
                    <LuCalendar className="size-4" />
                </span>
            </div>
        </div>
    );

    const triggerElement = mode === 'range' ? rangeTriggerElement : singleTriggerElement;

    return (
        <div
            className={cn(
                'flex gap-2.5 min-w-0',
                isHorizontal ? 'flex-row items-center justify-start' : 'flex-col items-start',
                className,
            )}
        >
            {label && (
                <Label
                    htmlFor={id}
                    className={cn(
                        isHorizontal ? 'w-[125px] shrink-0 justify-end' : 'w-full px-0 py-0 justify-start',
                        labelClassName,
                    )}
                >
                    {label}
                </Label>
            )}
            <div className={cn('flex flex-col min-w-0', isHorizontal ? 'flex-1' : 'w-full')}>
                {hideDays ? (
                    triggerElement
                ) : (
                    <Calendar
                        {...calendarProps}
                        popover
                        trigger={triggerElement}
                        open={open}
                        onOpenChange={handleOpenChange}
                        showDropdowns={showDropdowns}
                    />
                )}
                {hasValidationError && (
                    <span className="text-[10px] leading-[1.46] text-alert-error mt-1">
                        {t('dateInput.validationError')}
                    </span>
                )}
                {hasError && <span className="text-[10px] leading-[1.46] text-alert-error mt-1">{error}</span>}
            </div>
        </div>
    );
};
